import {
    LoopExpanded,
    useCalculateLstBreakevenTime,
    getSimpleInterest,
    BsMetaUtil,
    UseBreakevenHook,
    BreakevenBaseParams,
    useCalculatePtBreakevenTime,
    useOraclePrices
} from "@bridgesplit/abf-react";
import { LoopWindType } from "@bridgesplit/abf-sdk";
import { Row, Column, Span, StatColumn, Text, Tooltip, Icon } from "@bridgesplit/ui";
import {
    bsMath,
    formatSeconds,
    formatTokenAmount,
    SOL_DECIMALS,
    SOL_SYMBOL,
    TIME,
    TIME_IN_DAYS,
    WRAPPED_SOL_MINT
} from "@bridgesplit/utils";
import { LoopDisplayType } from "app/components/common";
import { useTransactionData } from "app/components/transactions";
import { TransactionTypes } from "app/types";

import { formatExecutionLossValue } from "../execution-detail/formatters";
import { useWindExecutionDetails } from "./util";
import { useWindContext } from "./WindContext";

enum BreakevenMethod {
    Lst,
    Pt
}

type HeaderDescription = {
    method: string;
    horizon: string;
    warning: string | null;
};

const BREAKEVEN_CONFIG: Record<BreakevenMethod, HeaderDescription> = {
    [BreakevenMethod.Lst]: {
        method: "epoch-based staking rewards (compounded every 2 days)",
        horizon: "90 days",
        warning: null
    },
    [BreakevenMethod.Pt]: {
        method: "daily fixed-yield accrual",
        horizon: "3 years",
        warning: null
    }
};

export function WindBreakEvenStats({ loopExpanded, isPtLoop }: { loopExpanded: LoopExpanded; isPtLoop: boolean }) {
    if (isPtLoop) return <PtBreakEven loopExpanded={loopExpanded} />;
    return <LstBreakEven loopExpanded={loopExpanded} />;
}

function BreakEven<TParams extends BreakevenBaseParams>({
    loopExpanded,
    useBreakevenHook,
    hookParams,
    method,
    collateralApy
}: {
    loopExpanded: LoopExpanded;
    useBreakevenHook: UseBreakevenHook<TParams>;
    hookParams: TParams;
    method: BreakevenMethod;
    collateralApy: number | undefined;
}) {
    const { externalQuote, quote: windQuote, form, oraclePrices } = useWindContext();
    const { getUsdPrice } = useOraclePrices([WRAPPED_SOL_MINT]);
    const solUsdPrice = getUsdPrice(WRAPPED_SOL_MINT);
    const { getTransactionFees } = useTransactionData();

    const principalOracle = loopExpanded?.principalOracle;
    const collateralOracle = oraclePrices?.get(loopExpanded.collateralToken.mint);

    const { executionDetails } = useWindExecutionDetails({
        externalQuote,
        form,
        principalOracle,
        collateralOracle
    });

    //Get transaction fee for Loop Wind
    const transactionData = getTransactionFees(TransactionTypes.EarnBorrow);
    //Convert transactoion fee to principal oracle price
    const transactionFeeUsd = transactionData.totalFeeAmount * (solUsdPrice ?? 0);
    //Get execution loss in USD
    const executionLossUsd = -(executionDetails?.executionLossAmount ?? 0);
    //Calculate closing fee in USD, by multiplying execution loss and transaction fee
    const collateralUsdPrice = collateralOracle?.usdPrice ?? 0;
    const closingFee = collateralUsdPrice > 0 ? (executionLossUsd + transactionFeeUsd) / collateralUsdPrice : 0;
    //Calculate total fees in USD, by multiplying closing fee by 2 (open/close)
    const feesUsd = (executionLossUsd + transactionFeeUsd) * 2;

    const maxDuration = useBreakevenHook({
        ...hookParams,
        feesUsd
    });

    if (!externalQuote || !windQuote || !form.inputAmount) return null;

    const interestPerDay = getSimpleInterest({
        principal: windQuote.principalAmount,
        apy: windQuote.bestQuote.apy,
        loanDuration: TIME.DAY,
        subtractPrincipal: true
    });

    return (
        <Row spacing={0.5}>
            <Text>{formatDuration(maxDuration, method)}</Text>
            <Tooltip
                minTooltipWidth="350px"
                reverseColors
                placement="bottom"
                title={
                    <Column spacing={2} p={1}>
                        <TooltipHeader maxDuration={maxDuration} method={method} />
                        <StatColumn
                            variant="body2"
                            captionVariant="body2"
                            stats={[
                                {
                                    caption: "Position setup cost",
                                    value: formatTokenAmount(transactionData.totalFeeAmount, {
                                        decimals: SOL_DECIMALS,
                                        symbol: SOL_SYMBOL
                                    })
                                },
                                {
                                    caption: "Est. close impact",
                                    value: BsMetaUtil.formatAmount(loopExpanded.collateralToken, closingFee),
                                    tooltip:
                                        "Closing costs vary and cannot be precisely estimated. This estimate combines setup cost and current execution impact to give a worst-case estimate"
                                },
                                {
                                    caption: "Est. execution impact",
                                    value: formatExecutionLossValue({
                                        executionDetails,
                                        principalToken: loopExpanded.principalToken,
                                        collateralToken: loopExpanded.collateralToken,
                                        principalUsdPrice: principalOracle?.usdPrice,
                                        collateralUsdPrice: collateralOracle?.usdPrice,
                                        displayTokenType: LoopWindType.Collateral,
                                        displayType: LoopDisplayType.TokenCostBasis
                                    })
                                },
                                {
                                    caption: "Interest per day",
                                    value: BsMetaUtil.formatAmount(loopExpanded.principalToken, interestPerDay)
                                },
                                {
                                    caption: "Est. daily yield",
                                    value: BsMetaUtil.formatAmount(loopExpanded.collateralToken, collateralApy)
                                }
                            ]}
                        />
                    </Column>
                }
            >
                <Text color="disabled">
                    <Icon type="tooltip" />
                </Text>
            </Tooltip>
        </Row>
    );
}

function TooltipHeader({ method }: { maxDuration: number | undefined; method: BreakevenMethod }) {
    const config = BREAKEVEN_CONFIG[method];

    return (
        <Text sx={{ display: "inline" }} color="caption" variant="body2">
            Breakeven is estimated by simulating{" "}
            <Span sx={{ color: (theme) => theme.palette.text.primary }}>{config.method}</Span> against{" "}
            <Span sx={{ color: (theme) => theme.palette.text.primary }}>daily loan growth and upfront fees</Span>.{" "}
            <Span sx={{ color: (theme) => theme.palette.warning.light }}>{config.warning}</Span>
        </Text>
    );
}

function LstBreakEven({ loopExpanded }: { loopExpanded: LoopExpanded }) {
    const { quote: windQuote, oraclePrices } = useWindContext();
    const totalBorrowUsd = bsMath.mul(windQuote?.principalAmount, loopExpanded?.principalOracle.usdPrice);
    const collateralOracle = oraclePrices?.get(loopExpanded.collateralToken.mint);
    const totalCollateralUsd = bsMath.mul(windQuote?.leveragedCollateralAmount, collateralOracle?.usdPrice);
    const dailyCollateralApy = windQuote?.netApyPct ? windQuote.netApyPct / TIME_IN_DAYS.YEAR : undefined;

    return (
        <BreakEven
            loopExpanded={loopExpanded}
            useBreakevenHook={useCalculateLstBreakevenTime}
            hookParams={{
                totalCollateralUsd,
                lstApy: windQuote?.netApyPct,
                loanApy: windQuote?.bestQuote.apy,
                totalDebtUsd: totalBorrowUsd
            }}
            method={BreakevenMethod.Lst}
            collateralApy={dailyCollateralApy}
        />
    );
}

function PtBreakEven({ loopExpanded }: { loopExpanded: LoopExpanded }) {
    const { quote: windQuote, oraclePrices } = useWindContext();
    const totalBorrowUsd = bsMath.mul(windQuote?.principalAmount, loopExpanded?.principalOracle.usdPrice);
    const collateralOracle = oraclePrices?.get(loopExpanded.collateralToken.mint);
    const totalCollateralUsd = bsMath.mul(windQuote?.leveragedCollateralAmount, collateralOracle?.usdPrice);

    const dailyCollateralApy = windQuote?.netApyPct ? windQuote.netApyPct / TIME_IN_DAYS.YEAR : undefined;
    return (
        <BreakEven
            loopExpanded={loopExpanded}
            useBreakevenHook={useCalculatePtBreakevenTime}
            hookParams={{
                totalCollateralUsd,
                ptApy: windQuote?.netApyPct,
                loanApy: windQuote?.bestQuote.apy,
                totalDebtUsd: totalBorrowUsd
            }}
            method={BreakevenMethod.Pt}
            collateralApy={dailyCollateralApy}
        />
    );
}

function formatDuration(duration: number | undefined, method: BreakevenMethod) {
    if (duration === undefined) {
        return "--";
    }
    if (duration === 0) {
        return "Instant";
    }
    if (duration === Number.MAX_SAFE_INTEGER) {
        const horizon = BREAKEVEN_CONFIG[method].horizon;
        return `+${horizon}`;
    }
    return formatSeconds(duration, { maxUnit: "day" });
}
